/**
 * WeatherService - Handles interactions with the weather.gov API
 * API Documentation: https://www.weather.gov/documentation/services-web-api
 */

const BASE_URL = "https://api.weather.gov";
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export interface WindData {
  speedMps: number | null; // Wind speed in meters per second
  directionDegrees: number | null; // Wind direction in degrees (0-360)
  timestamp: Date;
  stationId: string; // Weather station identifier (e.g., "KTEB")
  stationName?: string; // Weather station name (e.g., "Teterboro Airport")
}

interface CachedWindData {
  data: WindData;
  expiresAt: number;
}

interface PointMetadata {
  gridId: string;
  gridX: number;
  gridY: number;
}

interface ObservationStation {
  stationId: string;
  name: string;
}

// Simple in-memory cache
const cache = new Map<string, CachedWindData>();

export class WeatherService {
  private baseUrl: string;

  constructor(baseUrl: string = BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get wind data for a specific location
   * @param latitude - Location latitude
   * @param longitude - Location longitude
   * @returns Wind data or null if unavailable
   */
  async getWindData(latitude: number, longitude: number): Promise<WindData | null> {
    // Check cache first
    const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
    const cached = cache.get(cacheKey);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.data;
    }

    try {
      // Step 1: Get grid coordinates for this location
      const pointMeta = await this.getPointMetadata(latitude, longitude);
      if (!pointMeta) {
        console.warn(`[WeatherService] No point metadata for ${cacheKey}`);
        return null;
      }

      // Step 2: Get nearby weather stations
      const stations = await this.getNearbyStations(
        pointMeta.gridId,
        pointMeta.gridX,
        pointMeta.gridY
      );
      if (!stations || stations.length === 0) {
        console.warn(`[WeatherService] No weather stations found`);
        return null;
      }

      // Step 3: Try to get observation from available stations
      // Skip stations that return null values - try next station
      for (const station of stations) {
        const observation = await this.getLatestObservation(station.stationId, station.name);
        if (observation) {
          // Only accept observations with at least one valid wind value
          if (observation.speedMps !== null || observation.directionDegrees !== null) {
            // Cache the result
            cache.set(cacheKey, {
              data: observation,
              expiresAt: Date.now() + CACHE_DURATION_MS,
            });
            return observation;
          } else {
            console.warn(`[WeatherService] Station ${station.stationId} returned null wind values, trying next station`);
          }
        }
      }

      console.warn(`[WeatherService] No valid observations from any of ${stations.length} stations`);
      return null;
    } catch (error) {
      console.error("[WeatherService] Error fetching wind data:", error);
      return null;
    }
  }

  /**
   * Get point metadata (grid coordinates) for a lat/lng location
   */
  private async getPointMetadata(
    latitude: number,
    longitude: number
  ): Promise<PointMetadata | null> {
    try {
      const url = `${this.baseUrl}/points/${latitude.toFixed(4)},${longitude.toFixed(4)}`;
      const response = await fetch(url, {
        headers: {
          "User-Agent": "AirPlx (contact@airplx.com)",
        },
      });

      if (!response.ok) {
        console.warn(`Point metadata request failed: ${response.status}`);
        return null;
      }

      const data = await response.json();
      if (!data?.properties?.gridId || !data?.properties?.gridX || !data?.properties?.gridY) {
        return null;
      }

      return {
        gridId: data.properties.gridId,
        gridX: data.properties.gridX,
        gridY: data.properties.gridY,
      };
    } catch (error) {
      console.error("Error fetching point metadata:", error);
      return null;
    }
  }

  /**
   * Get nearby weather stations for a grid location
   */
  private async getNearbyStations(
    gridId: string,
    gridX: number,
    gridY: number
  ): Promise<ObservationStation[]> {
    try {
      const url = `${this.baseUrl}/gridpoints/${gridId}/${gridX},${gridY}/stations`;
      const response = await fetch(url, {
        headers: {
          "User-Agent": "AirPlx (contact@airplx.com)",
        },
      });

      if (!response.ok) {
        console.warn(`Stations request failed: ${response.status}`);
        return [];
      }

      const data = await response.json();
      if (!data?.features || !Array.isArray(data.features)) {
        return [];
      }

      return data.features
        .map((feature: any) => ({
          stationId: feature.properties?.stationIdentifier,
          name: feature.properties?.name,
        }))
        .filter((station: any) => station.stationId);
    } catch (error) {
      console.error("Error fetching stations:", error);
      return [];
    }
  }

  /**
   * Get latest observation from a specific weather station
   */
  private async getLatestObservation(stationId: string, stationName?: string): Promise<WindData | null> {
    try {
      const url = `${this.baseUrl}/stations/${stationId}/observations/latest`;
      const response = await fetch(url, {
        headers: {
          "User-Agent": "AirPlx (contact@airplx.com)",
        },
      });

      if (!response.ok) {
        console.warn(`Observation request failed for ${stationId}: ${response.status}`);
        return null;
      }

      const data = await response.json();
      if (!data?.properties) {
        return null;
      }

      const props = data.properties;

      // Extract wind speed and convert to m/s
      let speedMps: number | null = null;
      if (props.windSpeed?.value !== null && props.windSpeed?.value !== undefined) {
        const rawValue = props.windSpeed.value;
        const unitCode = props.windSpeed.unitCode;

        // Convert to m/s based on unit code
        if (unitCode === "wmoUnit:km_h-1") {
          // Convert km/h to m/s
          speedMps = rawValue / 3.6;
        } else if (unitCode === "wmoUnit:m_s-1" || !unitCode) {
          // Already in m/s or no unit specified (assume m/s)
          speedMps = rawValue;
          if (!unitCode) {
            console.warn(`[WeatherService] No unitCode for windSpeed, assuming m/s`);
          }
        } else {
          console.warn(`[WeatherService] Unknown windSpeed unitCode: ${unitCode}, assuming m/s`);
          speedMps = rawValue;
        }
      }

      // Extract wind direction (in degrees)
      let directionDegrees: number | null = null;
      if (props.windDirection?.value !== null && props.windDirection?.value !== undefined) {
        directionDegrees = props.windDirection.value;
      }

      // Extract timestamp
      const timestamp = props.timestamp ? new Date(props.timestamp) : new Date();

      return {
        speedMps,
        directionDegrees,
        timestamp,
        stationId,
        stationName,
      };
    } catch (error) {
      console.error(`Error fetching observation for ${stationId}:`, error);
      return null;
    }
  }

  /**
   * Clear the cache (useful for testing)
   */
  static clearCache(): void {
    cache.clear();
  }
}
